import ApiClient from "../helpers/ApiClient";
import { API } from "../config/config";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import useSWR from "swr";

// Ensure auth token is set for every request
const ensureAuth = () => {
  const userDetails = getUserDetailsInLocalStorage();
  if (userDetails?.token) {
    ApiClient.defaults.headers.common["Authorization"] = `Bearer ${userDetails.token}`;
  }
};

const fetcher = async (url) => {
  ensureAuth();
  const response = await ApiClient.get(url);
  return response.data;
};

// ══════════════════════════════════════════════════════════════
// WEBSITE SETTINGS
// ══════════════════════════════════════════════════════════════
export function useWebsiteSettings() {
  const APIURL = `${API}/website-crm/settings`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading, mutate, APIURL };
}

export async function updateWebsiteSettings(settings) {
  ensureAuth();
  const response = await ApiClient.post(`${API}/website-crm/settings`, settings);
  return response;
}

// ══════════════════════════════════════════════════════════════
// PACKAGES
// ══════════════════════════════════════════════════════════════
export function useWebsitePackages() {
  const APIURL = `${API}/website-crm/packages`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading, mutate, APIURL };
}

export async function addWebsitePackage(pkg) {
  ensureAuth();
  return await ApiClient.post(`${API}/website-crm/packages`, pkg);
}

export async function updateWebsitePackage(id, pkg) {
  ensureAuth();
  return await ApiClient.patch(`${API}/website-crm/packages/${id}`, pkg);
}

export async function deleteWebsitePackage(id) {
  ensureAuth();
  return await ApiClient.delete(`${API}/website-crm/packages/${id}`);
}

// ══════════════════════════════════════════════════════════════
// PRODUCTS
// ══════════════════════════════════════════════════════════════
export function useWebsiteProducts() {
  const APIURL = `${API}/website-crm/products`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading, mutate, APIURL };
}

export async function addWebsiteProduct(product) {
  ensureAuth();
  return await ApiClient.post(`${API}/website-crm/products`, product);
}

export async function updateWebsiteProduct(id, product) {
  ensureAuth();
  return await ApiClient.patch(`${API}/website-crm/products/${id}`, product);
}

export async function deleteWebsiteProduct(id) {
  ensureAuth();
  return await ApiClient.delete(`${API}/website-crm/products/${id}`);
}

// ══════════════════════════════════════════════════════════════
// STORIES (YouTube)
// ══════════════════════════════════════════════════════════════
export function useWebsiteStories() {
  const APIURL = `${API}/website-crm/stories`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading, mutate, APIURL };
}

export async function addWebsiteStory(story) {
  ensureAuth();
  return await ApiClient.post(`${API}/website-crm/stories`, story);
}

export async function updateWebsiteStory(id, story) {
  ensureAuth();
  return await ApiClient.patch(`${API}/website-crm/stories/${id}`, story);
}

export async function deleteWebsiteStory(id) {
  ensureAuth();
  return await ApiClient.delete(`${API}/website-crm/stories/${id}`);
}

// ══════════════════════════════════════════════════════════════
// REVIEWS
// ══════════════════════════════════════════════════════════════
export function useWebsiteReviews() {
  const APIURL = `${API}/website-crm/reviews`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading, mutate, APIURL };
}

export async function addWebsiteReview(review) {
  ensureAuth();
  return await ApiClient.post(`${API}/website-crm/reviews`, review);
}

export async function updateWebsiteReview(id, review) {
  ensureAuth();
  return await ApiClient.patch(`${API}/website-crm/reviews/${id}`, review);
}

export async function deleteWebsiteReview(id) {
  ensureAuth();
  return await ApiClient.delete(`${API}/website-crm/reviews/${id}`);
}

// ══════════════════════════════════════════════════════════════
// PUBLIC WEBSITE DATA (no auth)
// ══════════════════════════════════════════════════════════════
export function usePublicWebsiteData() {
  const APIURL = `${API}/website-crm/public`;
  const { data, error, isLoading } = useSWR(APIURL, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  return { data, error, isLoading };
}

// ══════════════════════════════════════════════════════════════
// GOOGLE DRIVE HELPER - convert sharing link to direct image URL
// ══════════════════════════════════════════════════════════════
export function googleDriveToDirectUrl(url) {
  if (!url) return null;
  // Handle Google Drive sharing URLs
  // Format: https://drive.google.com/file/d/FILE_ID/view?...
  const driveMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    return `https://lh3.googleusercontent.com/d/${driveMatch[1]}`;
  }
  // Format: https://drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://lh3.googleusercontent.com/d/${openMatch[1]}`;
  }
  // Already a direct URL
  return url;
}
